import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/atoms";
import { useSiteSettings } from "@/hooks/use-site-settings";
import {
  MIN_AGE,
  hasAffirmedAge,
  recordAgeAffirmation,
} from "@/lib/compliance";

/**
 * DRB-4 — 21+ affirmation interstitial.
 *
 * Structurally mirrors CookieConsent (the only pre-existing interstitial in this codebase), with
 * three deliberate differences, each of which is the point of the component:
 *
 *  1. It is BLOCKING (`fixed inset-0`), not a dismissible corner banner. A cookie notice you can
 *     ignore is correct; an age gate you can ignore is decorative.
 *  2. It renders NOTHING until the stored affirmation has actually been read. Defaulting to
 *     "hidden" for one paint flashes the shop to a minor; defaulting to "shown" flashes the gate
 *     at every returning adult. The third state — "not yet known" — is neither, so it gets its own
 *     value rather than being collapsed into one of the other two.
 *  3. Declining is a terminal state with no way past it, not a close button.
 *
 * WHAT THIS IS AND IS NOT: a self-attestation gate, which is the industry floor for hemp-derived
 * CBD and what nearly every comparable retailer ships. It is NOT identity verification. If counsel
 * requires documentary proof of age, that is a third-party ID service and a separate piece of work
 * — this component is where it would mount, and this comment is here so nobody mistakes the floor
 * for the ceiling.
 */
export function AgeGate() {
  // Three states, never two. `null` = not yet read from storage.
  const [affirmed, setAffirmed] = useState<boolean | null>(null);
  const [declined, setDeclined] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const { settings } = useSiteSettings();
  // `!== false` — a settings doc written before DRB-4 has no such field, and for an
  // age-restricted category the safe resolution of "unknown" is GATED. (useSiteSettings seeds
  // from the static defaults before Firestore answers, so this is never momentarily undefined.)
  const enabled = settings.ageGateEnabled !== false;

  useEffect(() => {
    setAffirmed(hasAffirmedAge());
  }, []);

  const blocking = enabled && affirmed === false;

  // Lock the page behind the gate. Without this the shop scrolls under the overlay on iOS and
  // the "block" is purely visual.
  useEffect(() => {
    if (!blocking) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    confirmRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
    };
  }, [blocking]);

  if (!blocking) return null;

  function confirm() {
    recordAgeAffirmation();
    setAffirmed(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      // No backdrop-blur: scroll-perf-gate rule D flags backdrop-filter on a fixed element, and
      // it is right. The scrim is already 95% opaque, so the blur was imperceptible while still
      // costing a full backdrop re-sample per frame on any device that composites it.
      className="fixed inset-0 z-[100] flex items-center justify-center bg-night-blue/95 px-4"
    >
      <div className="w-full max-w-md rounded-card bg-white p-7 text-center shadow-xl">
        <img
          src="/brand/logo.png"
          alt=""
          aria-hidden="true"
          className="mx-auto mb-5 h-10 w-auto"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />

        {declined ? (
          <>
            <h2
              id="age-gate-title"
              className="font-condensed text-2xl font-bold text-night-blue"
            >
              Sorry, we can't let you in
            </h2>
            <p className="mt-3 text-sm text-night-blue/80">
              You must be {MIN_AGE} or older to shop DrBurns&rsquo; ReLeaf.
              Thanks for stopping by.
            </p>
            <div className="mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeclined(false)}
              >
                Go back
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2
              id="age-gate-title"
              className="font-condensed text-2xl font-bold text-night-blue"
            >
              Are you {MIN_AGE} or older?
            </h2>
            <p className="mt-3 text-sm text-night-blue/80">
              You must be {MIN_AGE} or older to browse and purchase our
              products.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button ref={confirmRef} size="lg" onClick={confirm}>
                Yes, I&rsquo;m {MIN_AGE} or older
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setDeclined(true)}
              >
                No, I&rsquo;m not
              </Button>
            </div>
            <p className="mt-5 text-xs text-night-blue/70">
              By entering you agree to our{" "}
              <Link to="/legal/terms" className="underline">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/legal/privacy" className="underline">
                Privacy Policy
              </Link>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
